import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, CheckCheck, Pause, Play, RotateCcw } from 'lucide-react';
import { cancelJob, pauseJob, resolveJob, resumeJob, retryJob } from '@mkcore/platform/schema';
import { Badge, Button, Card, ErrorState, Mono, PageHeader, Skeleton, TBody, THead, Table, Td, Th, Tr, toast } from '@mkcore/ui';
import { formatDateTime } from '@/lib/format';
import { useActionMutation, useErrorMessage } from '@/lib/mutation';
import { useCan } from '@/lib/session';
import { jobQuery } from '@/features/admin/queries';
import { JobStatusBadge, formatDuration } from '@/features/admin/job-status';
import { ConfirmButton } from '@/components/confirm';

export const Route = createFileRoute('/_app/admin/jobs/$id')({ component: JobDetail });

function Json({ value }: { value: unknown }) {
  return <pre className="max-h-64 overflow-auto rounded-md bg-mono p-3 font-mono text-xs">{JSON.stringify(value, null, 2)}</pre>;
}

const levelTone = { info: 'outline', warn: 'warning', error: 'danger' } as const;

/**
 * Run detail (CO-0701, CO-0966): the steps with attempts and duration, the log lines of the steps as a timeline, the
 * runs it started, input and output (masked), and resume, pause, cancel or resolve (CO-0702, CO-0721, CO-0964).
 */
function JobDetail() {
  const { t } = useTranslation();
  const { id } = Route.useParams();
  const navigate = useNavigate();
  const job = useQuery({ ...jobQuery(id), refetchInterval: (q) => (['running', 'waiting'].includes(q.state.data?.status ?? '') ? 3000 : false) });
  const canUpdate = useCan('admin:update');
  const message = useErrorMessage();
  const invalidate = [['admin', 'job']];
  const retry = useActionMutation(retryJob, {
    invalidate,
    onSuccess: (r) => {
      toast.success(t('jobs.retried'));
      void navigate({ to: '/admin/jobs/$id', params: { id: r.workflowId } });
    },
  });
  const cancel = useActionMutation(cancelJob, { invalidate });
  const pause = useActionMutation(pauseJob, { invalidate, success: t('jobs.paused') });
  const resume = useActionMutation(resumeJob, { invalidate, success: t('jobs.resumed') });
  const resolve = useActionMutation(resolveJob, { invalidate, success: t('jobs.resolvedDone') });
  if (job.isError) return <ErrorState title={t('errors.generic')} description={message(job.error)} />;
  if (!job.data) return <Skeleton className="h-96" />;
  const j = job.data;
  const active = j.status === 'running' || j.status === 'waiting';
  return (
    <>
      <Link to="/admin/jobs" className="mb-3 inline-flex items-center gap-1 text-sm text-fg-2 hover:text-fg-1">
        <ArrowLeft className="size-4" /> {t('jobs.title')}
      </Link>
      <PageHeader
        eyebrow={t('nav.jobs')}
        title={j.jobType}
        description={
          <span className="grid gap-0.5">
            <Mono className="text-xs">{j.workflowId}</Mono>
            <span className="text-xs text-fg-3">
              {t(`jobs.triggers.${j.trigger}`, { defaultValue: j.trigger })} · {formatDateTime(j.createdAt)} · {formatDuration(j.durationMs)}
              {j.targetType && ` · ${j.targetType} ${j.targetId ?? ''}`}
            </span>
            {j.parentWorkflowId && (
              <span className="text-xs">
                {t('jobs.parent')}:{' '}
                <Link to="/admin/jobs/$id" params={{ id: j.parentWorkflowId }} className="hover:underline">
                  <Mono className="text-xs">{j.parentWorkflowId}</Mono>
                </Link>
              </span>
            )}
          </span>
        }
        actions={
          <>
            <JobStatusBadge status={j.status} outcome={j.outcome} stuck={!!j.stuckAt} resolved={!!j.resolvedAt} />
            {canUpdate && (j.status === 'failed' || j.status === 'cancelled') && (
              <Button loading={retry.isPending} onClick={() => retry.mutate({ input: { id } })}>
                <RotateCcw /> {t('jobs.retry')}
              </Button>
            )}
            {canUpdate && (j.status === 'paused' || j.status === 'cancelled' || (j.stuckAt && active)) && (
              <Button variant="secondary" loading={resume.isPending} onClick={() => resume.mutate({ input: { id } })}>
                <Play /> {t('jobs.resume')}
              </Button>
            )}
            {canUpdate && active && (
              <Button variant="secondary" loading={pause.isPending} onClick={() => pause.mutate({ input: { id } })}>
                <Pause /> {t('jobs.pause')}
              </Button>
            )}
            {canUpdate && active && <ConfirmButton label={t('jobs.cancel')} title={t('jobs.cancel')} onConfirm={() => cancel.mutateAsync({ input: { id } })} />}
            {canUpdate && j.status === 'failed' && !j.resolvedAt && (
              <ConfirmButton
                icon={<CheckCheck className="size-4" aria-hidden />}
                label={t('jobs.resolve')}
                title={t('jobs.resolveTitle')}
                description={t('jobs.resolveDescription')}
                onConfirm={() => resolve.mutateAsync({ input: { id } })}
              />
            )}
          </>
        }
      />
      {j.error && (
        <Card className="mb-4 border-danger/40 bg-danger-bg p-4 text-sm text-danger" role="alert">
          <strong>{t('jobs.error')}:</strong> {j.error}
        </Card>
      )}
      <h2 className="mb-2 text-lg font-bold">{t('jobs.steps')}</h2>
      <Table>
        <THead>
          <Tr>
            <Th>#</Th>
            <Th>{t('common.name')}</Th>
            <Th>{t('common.status')}</Th>
            <Th className="text-right">{t('jobs.attempts')}</Th>
            <Th className="hidden md:table-cell text-right">{t('jobs.duration')}</Th>
            <Th className="hidden md:table-cell">{t('common.updated')}</Th>
          </Tr>
        </THead>
        <TBody>
          {j.steps.map((s) => (
            <Tr key={s.stepId}>
              <Td className="tabular text-fg-3">{s.stepId}</Td>
              <Td>
                <Mono className="text-xs">{s.name}</Mono>
                {s.childWorkflowId && (
                  <div>
                    <Link to="/admin/jobs/$id" params={{ id: s.childWorkflowId }} className="text-xs hover:underline">
                      <Mono className="text-[11px]">{s.childWorkflowId}</Mono>
                    </Link>
                  </div>
                )}
                {s.error && <div className="mt-1 text-xs text-danger">{s.error}</div>}
              </Td>
              <Td>
                <JobStatusBadge status={s.status === 'failed' ? 'failed' : s.status === 'succeeded' ? 'succeeded' : 'running'} />
              </Td>
              <Td className={`text-right tabular ${s.attempts > 1 ? 'font-bold text-warning' : 'text-fg-2'}`}>{s.attempts}</Td>
              <Td className="hidden md:table-cell text-right tabular text-fg-2">{formatDuration(s.durationMs)}</Td>
              <Td className="hidden md:table-cell tabular text-fg-2">{s.completedAt ? formatDateTime(s.completedAt) : '—'}</Td>
            </Tr>
          ))}
        </TBody>
      </Table>

      <h2 className="mb-2 mt-6 text-lg font-bold">{t('jobs.logs')}</h2>
      {j.logs.length === 0 ? (
        <p className="text-sm text-fg-3">{t('jobs.noLogs')}</p>
      ) : (
        <ol className="grid gap-1 rounded-md border border-line p-3 text-xs" data-testid="job-logs">
          {j.logs.map((l, i) => (
            <li key={i} className="flex flex-wrap items-baseline gap-2">
              <span className="tabular text-fg-3">{formatDateTime(l.at)}</span>
              <Badge tone={levelTone[l.level]}>{l.level}</Badge>
              <Mono className="text-[11px] text-fg-3">
                {l.step}
                {l.attempt > 1 && ` #${l.attempt}`}
              </Mono>
              <span>{l.msg}</span>
              {l.fields && <Mono className="text-[11px] text-fg-2">{JSON.stringify(l.fields)}</Mono>}
            </li>
          ))}
        </ol>
      )}

      {j.children.length > 0 && (
        <>
          <h2 className="mb-2 mt-6 text-lg font-bold">{t('jobs.children')}</h2>
          <Table>
            <TBody>
              {j.children.map((c) => (
                <Tr key={c.workflowId}>
                  <Td>
                    <Link to="/admin/jobs/$id" params={{ id: c.workflowId }} className="font-bold hover:underline">
                      {c.jobType}
                    </Link>
                    <div>
                      <Mono className="text-[11px]">{c.workflowId}</Mono>
                    </div>
                  </Td>
                  <Td>
                    <JobStatusBadge status={c.status} outcome={c.outcome} />
                  </Td>
                  <Td className="text-right tabular text-fg-2">{formatDuration(c.durationMs)}</Td>
                </Tr>
              ))}
            </TBody>
          </Table>
        </>
      )}

      <div className="mt-6 grid gap-4 md:grid-cols-2">
        <div>
          <h2 className="mb-2 text-sm font-bold">{t('jobs.input')}</h2>
          <Json value={j.input} />
        </div>
        <div>
          <h2 className="mb-2 text-sm font-bold">{t('jobs.output')}</h2>
          <Json value={j.output} />
        </div>
      </div>
    </>
  );
}
